/**
 * 宿主 / iframe / 后端来源白名单。
 *
 * 约束来源：CONTRACT.md 第四节 iframe 接入要求。
 * - postMessage 必须使用固定 targetOrigin，不能使用 '*'；
 * - 必须同时校验 event.origin 与 event.source；
 * - 配置匹配的 CSP / frame-ancestors。
 * 这里只做"精确来源匹配"，不支持通配符子域，避免出现隐式放行。
 */

/** 归一化 origin：只保留 scheme://host[:port]，去掉路径/查询/哈希与末尾斜杠。 */
export function normalizeOrigin(value: string): null | string {
  const raw = value.trim();
  if (raw === '') return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * 精确来源白名单。allowlist 内的条目在构造时归一化并去重。
 */
export class OriginAllowlist {
  private readonly origins: ReadonlySet<string>;

  constructor(origins: Iterable<string>) {
    const set = new Set<string>();
    for (const item of origins) {
      const normalized = normalizeOrigin(item);
      if (normalized) set.add(normalized);
    }
    this.origins = set;
  }

  get size(): number {
    return this.origins.size;
  }

  has(origin: null | string | undefined): boolean {
    if (!origin) return false;
    const normalized = normalizeOrigin(origin);
    return normalized !== null && this.origins.has(normalized);
  }

  list(): string[] {
    return [...this.origins];
  }

  /** 出站时使用的 targetOrigin：必须唯一确定，否则拒绝发送。 */
  requireTarget(): string {
    const only = this.list();
    if (only.length !== 1 || only[0] === undefined) {
      throw new Error(
        'targetOrigin 必须唯一确定：请为 postMessage 配置恰好一个对端 origin',
      );
    }
    return only[0];
  }
}

/** 构造 widget 端（iframe 内）需要校验的宿主来源白名单。 */
export function createHostAllowlist(hostOrigins: Iterable<string>): OriginAllowlist {
  return new OriginAllowlist(hostOrigins);
}

/** 构造宿主端需要校验的 widget 来源白名单（同时用于 targetOrigin）。 */
export function createWidgetAllowlist(widgetOrigin: string): OriginAllowlist {
  return new OriginAllowlist([widgetOrigin]);
}